import {
  buildCases,
  recordKey,
  type CaseGraph,
  type CaseLink,
  type CaseRecordInput,
  type PropertyCase,
} from './cases';
import { propertyRecordKindLabel } from './field-labels';
import { classifySourceState, normalizeStatus } from './issues/status';
import { novSummary, recordHeadline, recordThread } from './property-feed';
import type { DisplayRecord } from './property-records';
import { documentTime } from './record-dates';
import type { NormalizedPage, RecordKind } from './types';

export { recordKey };
export type { CaseGraph, PropertyCase };

/** SFFD writes each finding as its own violation row under one inspection number. */
export function fireInspectionNumber(record: DisplayRecord) {
  if (record.kind !== 'fire-violation') return '';
  return record.pages?.[0]?.fields.inspection_number?.trim() ?? '';
}

/** Identifiers the DBI complaint and NOV pages link to each other with. */
function pageLinkedIds(record: DisplayRecord) {
  const ids = new Set<string>();
  const inspection = fireInspectionNumber(record);
  if (inspection) ids.add(inspection);
  for (const page of record.pages ?? []) {
    for (const link of page.links) {
      if (!/(?:NOV|Complaint|Permit)Details/i.test(link.url)) continue;
      try {
        const url = new URL(link.url);
        const id =
          url.searchParams.get('COMP_ID') ??
          url.searchParams.get('PermitNumber') ??
          url.searchParams.get('Permit_Number');
        if (id && id !== record.id) ids.add(id);
      } catch {
        // Ignore malformed city links.
      }
    }
  }
  return [...ids];
}

function pageText(record: DisplayRecord) {
  const text: string[] = [];
  for (const page of record.pages ?? []) {
    for (const value of Object.values(page.fields)) if (value) text.push(value);
    for (const table of page.tables) for (const row of table.rows) text.push(row.join(' '));
  }
  return text;
}

const COMPLETABLE_KINDS = new Set<RecordKind>([
  'building',
  'electrical',
  'plumbing',
  'complaint',
  'nov',
  'fire-violation',
  'fire-inspection',
  '311',
]);

/** A lapsed permit leaves work unsigned-off; it still needs renewing or closing. */
const LAPSED = /^expired$/;

/**
 * Closures that record work actually done: a finaled permit, an abated
 * notice. Anchored so "not abated" or "non compliance" never read as done.
 */
const PERMIT_RESOLVED = /^(?:complete[d]?|final(?:ed)?)$/;
const ENFORCEMENT_RESOLVED = /^(?:case\s+)?(?:abated|corrected|complied|(?:in\s+)?compliance)$/;
const RESOLVED = new Map<RecordKind, RegExp>([
  ['building', PERMIT_RESOLVED],
  ['electrical', PERMIT_RESOLVED],
  ['plumbing', PERMIT_RESOLVED],
  ['complaint', ENFORCEMENT_RESOLVED],
  ['nov', ENFORCEMENT_RESOLVED],
  ['fire-violation', /^(?:case\s+)?(?:abated|corrected|complied|resolved)$/],
]);

export type MemberState = 'open' | 'closed' | 'resolved';

/**
 * DBI keeps a complaint's own status at "Closed" and records the abatement
 * only in its case history; a later reopen undoes it.
 */
function caseHistoryAbated(pages: NormalizedPage[]) {
  let abated = false;
  for (const page of pages) {
    for (const table of page.tables) {
      const column = table.headers.findIndex((header) => /^status$/i.test(header.trim()));
      if (column < 0) continue;
      for (const row of table.rows) {
        const status = normalizeStatus(row[column] ?? '');
        if (/^case\s+abated$/.test(status)) abated = true;
        else if (/reopen/.test(status)) abated = false;
      }
    }
  }
  return abated;
}

/**
 * Where a case member stands for the owner: still open, closed with nothing
 * done (a city-closed 311 report, a complaint dropped as no violation, a
 * cancelled permit), or resolved by work that was finished and signed off.
 * Recorded documents are archived from the day they are filed, an old issued
 * permit is merely historical and an expired one lapsed mid-work, so those
 * stay "open" here rather than reading as done.
 */
export function memberState(
  record: Pick<DisplayRecord, 'kind' | 'archived' | 'status' | 'closedAt'> &
    Partial<Pick<DisplayRecord, 'pages'>>,
): MemberState {
  if (!record.archived || !COMPLETABLE_KINDS.has(record.kind)) return 'open';
  const status = normalizeStatus(record.status ?? '');
  if (LAPSED.test(status)) return 'open';
  if (
    classifySourceState({
      recordKind: record.kind,
      status: record.status,
      closedAt: record.closedAt,
    }) !== 'closed'
  )
    return 'open';
  if (RESOLVED.get(record.kind)?.test(status)) return 'resolved';
  if (
    (record.kind === 'complaint' || record.kind === 'nov') &&
    caseHistoryAbated(record.pages ?? [])
  )
    return 'resolved';
  return 'closed';
}

/** Whether a case member reached a terminal state (resolved or merely closed). */
export function memberComplete(
  record: Pick<DisplayRecord, 'kind' | 'archived' | 'status' | 'closedAt'>,
) {
  return memberState(record) !== 'open';
}

const MEMBER_ORDER: Record<MemberState, number> = {
  open: 0,
  resolved: 1,
  closed: 2,
};

/** Unresolved members first, then resolved work, then plain closures; stable within each. */
export function sortMembers<T>(members: T[], state: (member: T) => MemberState) {
  return members
    .map((member, index) => ({
      member,
      index,
      rank: MEMBER_ORDER[state(member)],
    }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.member);
}

export function caseRecordInput(record: DisplayRecord): CaseRecordInput {
  const kind: RecordKind = record.kind;
  const kindLabel = propertyRecordKindLabel(kind);
  // Case evidence must not change when a generated display title arrives.
  const headline = recordHeadline({ ...record, ai: undefined }, kindLabel);
  return {
    id: record.id,
    kind,
    status: record.status,
    date: record.date,
    title: headline === kindLabel ? `${kindLabel} ${record.id}` : headline,
    description: record.description ?? '',
    sourceUrl: record.sourceUrl,
    archived: record.archived,
    openedAt: record.openedAt,
    closedAt: record.closedAt,
    text: pageText(record),
    linkedIds: pageLinkedIds(record),
    relatedPermitIds: (record.relatedPermits ?? []).map((permit) => permit.id),
    complianceDays: kind === 'nov' ? novSummary(record).days : null,
    // The thread already carries plan-review holds for building permits.
    thread: recordThread(record),
    addenda: [],
  };
}

/** Cases, links and dated events for every record on the page. */
export function propertyCases(
  records: DisplayRecord[],
  now = Date.now(),
  extraLinks: CaseLink[] = [],
  timeZone?: string | null,
) {
  return buildCases(records.map(caseRecordInput), {
    now,
    parseDate: documentTime,
    extraLinks,
    timeZone,
  });
}

export function caseMembers(item: PropertyCase, records: DisplayRecord[]): DisplayRecord[] {
  const byKey = new Map(records.map((record) => [recordKey(record), record]));
  return sortMembers(
    item.memberKeys
      .map((key) => byKey.get(key))
      .filter((record): record is DisplayRecord => Boolean(record)),
    memberState,
  );
}

export function caseFor(record: DisplayRecord, graph: CaseGraph) {
  const caseKey = graph.caseByRecord.get(recordKey(record));
  return graph.cases.find((candidate) => candidate.key === caseKey);
}

/** Records related through the case graph, with how they are related. */
export function caseRelations(record: DisplayRecord, graph: CaseGraph, records: DisplayRecord[]) {
  const key = recordKey(record);
  const caseKey = graph.caseByRecord.get(key);
  if (!caseKey) return [];
  const item = graph.cases.find((candidate) => candidate.key === caseKey);
  if (!item) return [];
  const byKey = new Map(records.map((entry) => [recordKey(entry), entry]));
  return item.links
    .filter((link) => link.source === key || link.target === key)
    .map((link) => ({
      link,
      record: byKey.get(link.source === key ? link.target : link.source),
    }))
    .filter((entry): entry is { link: typeof entry.link; record: DisplayRecord } =>
      Boolean(entry.record),
    );
}
